import React, { useState, useMemo, useCallback, useRef } from "react"
import type { IsoDate } from "helpers/types/dates"
import classNames from "classnames"
import { useDispatch } from "react-redux"
import { isNil } from "lodash"
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, TextField } from "@mui/material"

import FilterSelectors from "components/add-filter/filter-selectors"
import { Creators as CreatorCreators } from "model/creator"
import { OK, combineStatuses, type DataStatus } from "helpers/data-statuses"
import { useFilterOns } from "hooks/use-filter-ons"
import { isFilterValid } from "helpers/filters"
import type { Filter, FilterOption as FilterOptionType } from "resources/filter-ons"
import { useHistoricalFactorSensitivityAvailability } from "resources/filters"
import { useAppSelector } from "hooks/redux"
import { useDashboardId } from "connected-components/dashboard/context/dashboard-id"
import AddUniverse from "modules/securities-search-parameters/components/add-universe"
import { useAccountAlias, useDefaultCurrencyId } from "resources/account"
import { mapFilterOns, makeFormattedOptions } from "components/add-filter/helpers"
import { NUMERIC_OPERATORS } from "helpers/filters/filter-ons/numbers"

import { FILTER_GROUPS } from "../../constants"
import type { FilterOption, ToggleFiltersOpts } from "./index"
import useSecuritySearchFilter from "./hooks/use-security-search-filter"
import useFilterResultsCount from "./hooks/use-filter-results-count"
import useUniverseFilterOns from "./hooks/use-universe-filter-ons"

const EMPTY_ARRAY: Filter[] = []

interface CreateCustomSearchProps {
  toggleFilters: (opts?: Partial<ToggleFiltersOpts>) => void
  showFilters: boolean
  applyNewCustomSearch: (filter: FilterOption | null) => void
}

const DEFAULT_FILTER: Filter[] = [{
  filterOn: {
    label: "Market Cap",
    type: "descriptor",
    dataType: "millions",
    operators: NUMERIC_OPERATORS,
    value: "marketCapitalization",
  },
  operator: NUMERIC_OPERATORS.find(operator => operator.value === "gt") || NUMERIC_OPERATORS[0],
  values: [500],
}]

const DEFAULT_CUSTOM_SEARCH_NAME = "My Custom Universe"

export default function CreateCustomSearch({ toggleFilters, showFilters, applyNewCustomSearch }: CreateCustomSearchProps) {
  const dashboardId = useDashboardId()
  const modelId = useAppSelector((s) => s.dashboard.model[dashboardId]?.modelId ?? null)
  const endDate = useAppSelector((s) => s.dashboard.core[dashboardId]?.endDate ?? null)
  const [filters, setFilters] = useState<Filter[]>(DEFAULT_FILTER)
  const [universeFilters, setUniverseFilters] = useState<Filter[]>([])
  const [customSearchName, setCustomSearchName] = useState<string>(DEFAULT_CUSTOM_SEARCH_NAME)
  const account = useAccountAlias()
  const dispatch = useDispatch()
  // Resource hooks handle null modelId/endDate gracefully by returning no data
  const [historicalFactorSensitivityAvailability, historicalFactorSensitivityAvailabilityStatus] = useHistoricalFactorSensitivityAvailability(modelId as string, account, endDate as IsoDate)
  const currency = useDefaultCurrencyId()
  const customSearchNameRef = useRef<HTMLInputElement>(null)
  const securitySearchFilter = useSecuritySearchFilter(filters, universeFilters)

  const [filterOns, filterOnsStatus] = useFilterOns({ filter: securitySearchFilter, modelId: modelId as string, selectedDate: endDate as IsoDate, isHistoricalFactorSensitivityAvailable: historicalFactorSensitivityAvailability })

  const formattedOptions = useMemo(() => {
    if (filterOnsStatus !== OK || !filterOns) return EMPTY_ARRAY
    return makeFormattedOptions(filterOns)
  }, [filterOns, filterOnsStatus])

  function handleSetFilterOn(index: number, option: FilterOptionType): void {
    setFilters(currentFilters => mapFilterOns(currentFilters, index, option))
  }

  function handleUpdateUniverseFilters(newFilters: Filter[]): void {
    setUniverseFilters(newFilters)
  }

  const filterArray = useMemo(
    () => securitySearchFilter ? [securitySearchFilter] : [],
    [securitySearchFilter],
  )

  const [filterResults, filterResultsStatus] = useFilterResultsCount(filterArray, modelId as string, endDate as IsoDate, currency)

  const closeAndReset = useCallback(() => {
    toggleFilters({ open: false })
    setFilters(DEFAULT_FILTER)
    setUniverseFilters([])
    setCustomSearchName(DEFAULT_CUSTOM_SEARCH_NAME)
  }, [toggleFilters])

  const handleSaveCustomSearch = useCallback(() => {
    if (!securitySearchFilter) return
    const numberOfFilters = Object.values(securitySearchFilter).reduce((acc, value) => {
      if (Array.isArray(value)) {
        return acc + value.length
      }
      return acc + 1
    }, 0)
    dispatch(CreatorCreators.create("filters", `/api/${account}/filters/${modelId}`, {
      filter: securitySearchFilter,
      name: customSearchName,
      count: filterResults,
      nbFilters: numberOfFilters,
    }, {
      onSuccess: (result: { id: string }) => {
        applyNewCustomSearch({
          label: customSearchName,
          value: result.id,
          group: FILTER_GROUPS.SAVED_SEARCHES,
          nbFilters: numberOfFilters,
          filter: securitySearchFilter,
          count: filterResults ?? 0,
        })
        closeAndReset()
        return `Search "${customSearchName}" created successfully`
      },
    }))
  }, [customSearchName, securitySearchFilter, filterResults, closeAndReset, applyNewCustomSearch, account, modelId, dispatch])

  const isSaveButtonDisabled = useMemo(() => {
    const validFilters = [...filters, ...universeFilters].filter(filter => isFilterValid(filter))
    return validFilters.length === 0 || !customSearchName || filterResultsStatus !== OK
  }, [filters, universeFilters, customSearchName, filterResultsStatus])

  const [universeFilterOns, universeFilterOnsStatus] = useUniverseFilterOns(modelId as string)

  const combinedStatus: DataStatus = useMemo(() => combineStatuses([universeFilterOnsStatus, filterOnsStatus, historicalFactorSensitivityAvailabilityStatus] as DataStatus[]), [universeFilterOnsStatus, filterOnsStatus, historicalFactorSensitivityAvailabilityStatus])
  if (combinedStatus !== OK) {
    return null
  }

  const { portfoliosList, etfsList, basketsList, indexesList, watchlistsList } = universeFilterOns ?? {}

  return (
    <Dialog
      open={showFilters}
      onClose={closeAndReset}
      slotProps={{
        transition: {
          onEntered: () => customSearchNameRef.current?.focus(),
        },
      }}
    >
      <DialogTitle>Create Custom Search</DialogTitle>
      <DialogContent className="create-custom-search-content">
        <TextField
          className="custom-search-name-input"
          required
          variant="standard"
          size="small"
          label="Custom Search Name"
          value={customSearchName}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => setCustomSearchName(event.target.value)}
          margin="none"
          inputRef={customSearchNameRef}
        />
        <FilterSelectors
          filters={filters}
          currency={currency}
          formattedOptions={formattedOptions}
          setFilters={setFilters}
          handleClose={closeAndReset}
          forceShowAdditionalFilterComponent={true}
          additionalFilterComponent={<AddUniverse
            updateFilters={handleUpdateUniverseFilters}
            portfoliosList={portfoliosList}
            etfsList={etfsList}
            watchlistsList={watchlistsList}
            swapsList={basketsList}
            indexesList={indexesList}
            account={account}
            modelId={modelId}
            existingFilters={universeFilters}
          />}
          updateFilters={(filters) => setFilters(filters)}
          handleSetFilterOn={handleSetFilterOn}
        />
        {filterResultsStatus === OK && !isNil(filterResults) && <Typography variant="body1" className={classNames({ "no-results": filterResults === 0 })}>{(filterResults as number).toString()} results found for current filter.</Typography>}
      </DialogContent>
      <DialogActions>
        <Button variant="contained" onClick={closeAndReset}>Cancel</Button>
        <Button variant="contained" color="primary" disabled={isSaveButtonDisabled} onClick={handleSaveCustomSearch}>Save Custom Search</Button>
      </DialogActions>
    </Dialog>
  )
}
